import { TextInput } from "@/components/ui/Field";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import {
  normalizeRabattAusweis,
  normalizeRabattModus,
  type RabattAusweis,
  type RabattModus,
} from "@/lib/documentCalc";

export interface PositionRabattValue {
  rabatt_prozent?: number | null;
  rabatt_betrag?: number | null;
  rabatt_modus?: string | null;
  rabatt_ausweis?: string | null;
}

export function PositionRabattFields({
  value,
  disabled,
  onChange,
}: {
  value: PositionRabattValue;
  disabled?: boolean;
  onChange: (partial: {
    rabatt_prozent?: number;
    rabatt_betrag?: number;
    rabatt_modus?: RabattModus;
    rabatt_ausweis?: RabattAusweis;
  }) => void;
}) {
  const modus = normalizeRabattModus(value.rabatt_modus);
  const ausweis = normalizeRabattAusweis(value.rabatt_ausweis);
  const amount = modus === "betrag" ? Number(value.rabatt_betrag ?? 0) : Number(value.rabatt_prozent ?? 0);

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <TextInput
            label={modus === "betrag" ? "Rabatt €" : "Rabatt %"}
            type="number"
            step="0.01"
            min={0}
            value={String(Number.isFinite(amount) ? amount : 0)}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (modus === "betrag") onChange({ rabatt_betrag: next });
              else onChange({ rabatt_prozent: next });
            }}
            disabled={disabled}
            hint={
              modus === "betrag"
                ? "Betrag auf die Position, nicht je Einheit."
                : undefined
            }
          />
        </div>
        <SegmentedControl
          name="Rabattart"
          value={modus}
          onChange={(next) => onChange({ rabatt_modus: next })}
          disabled={disabled}
          options={[
            { value: "prozent", label: "%" },
            { value: "betrag", label: "€" },
          ]}
        />
      </div>
      <div className="space-y-1">
        <span className="text-xs text-label-secondary">Sichtbarkeit</span>
        <SegmentedControl
          name="Rabatt-Sichtbarkeit"
          value={ausweis}
          onChange={(next) => onChange({ rabatt_ausweis: next })}
          disabled={disabled}
          options={[
            { value: "intern", label: "Nur intern" },
            { value: "dokument", label: "Auf Rechnung" },
          ]}
        />
        <p className="text-xs text-label-tertiary">
          {ausweis === "dokument"
            ? "Ausgangspreis und Rabatt stehen auf dem Dokument."
            : "Wird abgezogen und der Gesamtbetrag neu berechnet. Der Kunde sieht nur den reduzierten Preis."}
        </p>
      </div>
    </div>
  );
}
